import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { useAuth } from '../../context/AuthContext';
import { useOperationsAPI } from '../../context/useOperationsAPI';
import { OperationsAssistantRecovery } from '../../services/operationsAssistantRecovery';
import {
  AssistantAction, AssistantConfiguration, AssistantDelegation, AssistantDelegationInput,
  AssistantDetail, AssistantSession, AssistantTurnInput, AssistantIntakeInput, assistantError, assistantRequestId, assistantActionSummary, delegationActive,
  operatorAssistantAPI as assistantAPI,
} from '../../services/operatorAssistant';

type PendingTurn = { sessionId: string; input: AssistantTurnInput; phase: 'sending' | 'unknown' };
type PendingDecision = { approve: boolean; phase: 'sending' | 'unknown' };
type IntakeState = {
  source: string; csv: string; filename: string; error: string; busy: boolean;
  pending: AssistantIntakeInput | null; receipt: AssistantAction | null;
};
type AssistantState = {
  eligible: boolean;
  drawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  configuration?: AssistantConfiguration;
  loading: boolean;
  error: string | null;
  dismissError: () => void;
  sessions: AssistantSession[];
  selectedId: string | null;
  selectSession: (id: string) => void;
  detail?: AssistantDetail;
  detailLoading: boolean;
  detailError: string | null;
  creating: boolean;
  createSession: () => Promise<void>;
  draft: string;
  setDraft: (value: string) => void;
  pendingTurn: PendingTurn | null;
  submit: () => Promise<void>;
  retryTurn: () => Promise<void>;
  checkResponse: () => Promise<void>;
  actions: AssistantAction[];
  actionsLoading: boolean;
  actionsError: string | null;
  decisions: Record<string, PendingDecision>;
  decide: (id: string, approve: boolean) => Promise<void>;
  delegate: (id: string, ruleId: string) => Promise<void>;
  delegations: AssistantDelegation[];
  delegationsLoading: boolean;
  delegationsError: string | null;
  delegationId: string;
  setDelegationId: (id: string) => void;
  createDelegation: (input: AssistantDelegationInput) => Promise<AssistantDelegation | undefined>;
  pauseDelegation: (id: string) => Promise<void>;
  delegationBusy: boolean;
  intake: IntakeState;
  updateIntake: (fields: Partial<IntakeState>) => void;
  prepareIntake: (input: AssistantIntakeInput) => Promise<AssistantAction | undefined>;
  refresh: () => Promise<void>;
};
const Context = createContext<AssistantState | null>(null);

type ProviderProps = { children: React.ReactNode; recovery?: OperationsAssistantRecovery | null };

export function OperatorAssistantProvider({ children, recovery }: ProviderProps) {
  const { user, operationsScope } = useAuth();
  return <ScopedAssistantProvider recovery={operationsScope ? recovery : undefined} key={operationsScope || `${user?.id || 'signed-out'}:${window.location.host}`}>{children}</ScopedAssistantProvider>;
}

function ScopedAssistantProvider({ children, recovery }: ProviderProps) {
  const { user, operationsScope } = useAuth();
  const operatorAssistantAPI = useOperationsAPI(assistantAPI);
  const location = useLocation();
  const client = useQueryClient();
  const host = window.location.host;
  const eligible = Boolean(user?.is_modal_operations && operationsScope) || (user?.role === 'admin' && !user.is_support_staff && !window.location.hostname.startsWith('globaladmin.'));
  const scope = operationsScope || `${user?.id || 'signed-out'}:${host}`;
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(() => recovery?.read().selectedId || null);
  const [draft, setDraft] = useState(() => recovery?.read().draft || '');
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [pendingTurn, setPendingTurn] = useState<PendingTurn | null>(null);
  const pendingRef = useRef<PendingTurn | null>(null);
  const [decisions, setDecisions] = useState<Record<string, PendingDecision>>({});
  const [delegationId, setDelegationId] = useState('');
  const [delegationBusy, setDelegationBusy] = useState(false);
  const sendingRef = useRef(false);
  const decidingRef = useRef(new Set<string>());
  const intakeLock = useRef(false);
  const [intake, setIntake] = useState<IntakeState>({ source: '', csv: '', filename: '', error: '', busy: false, pending: null, receipt: null });
  const isAssistantPage = ['/admin/assistant', '/operations/assistant'].includes(location.pathname);
  const active = eligible && (drawerOpen || isAssistantPage);
  const key = ['operator-assistant', scope];
  const config = useQuery([...key, 'config'], ({ signal }) => operatorAssistantAPI.configuration(signal), {
    enabled: active, retry: false, staleTime: 30000,
  });
  const readable = active && config.isSuccess;
  const sessions = useQuery([...key, 'sessions'], ({ signal }) => operatorAssistantAPI.sessions(signal), {
    enabled: readable, retry: false,
  });
  const detail = useQuery([...key, 'session', selectedId], ({ signal }) => operatorAssistantAPI.detail(selectedId!, signal), {
    enabled: readable && !!selectedId, retry: false,
  });
  const actions = useQuery([...key, 'actions'], ({ signal }) => operatorAssistantAPI.actions(signal), {
    enabled: readable, retry: false,
  });
  const delegations = useQuery([...key, 'delegations'], ({ signal }) => operatorAssistantAPI.delegations(signal), {
    enabled: readable, retry: false,
  });

  useEffect(() => {
    currentScope.current = scope;
    const saved = recovery?.read();
    setDrawerOpen(false); setSelectedId(saved?.selectedId || null); setDraft(saved?.draft || ''); setError(null);
    setPendingTurn(null); pendingRef.current = null; sendingRef.current = false;
    decidingRef.current.clear(); setDecisions({}); setDelegationId(''); setCreating(false); setDelegationBusy(false);
    return () => { currentScope.current = `disposed:${scope}`; void client.cancelQueries(['operator-assistant', scope]); client.removeQueries(['operator-assistant', scope]); };
  }, [scope, client, recovery]);
  useLayoutEffect(() => {
    // Submitted/uncertain turns are not unsent drafts. Resume only reads the
    // selected conversation; it never recreates a pending request or sends it.
    recovery?.write({ selectedId, draft: pendingTurn ? '' : draft });
  }, [recovery, selectedId, draft, pendingTurn]);
  useEffect(() => {
    if (['/admin/assistant', '/operations/assistant'].includes(location.pathname)) setDrawerOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!selectedId && sessions.data?.length) setSelectedId(sessions.data[0].id);
  }, [selectedId, sessions.data]);
  useEffect(() => {
    if (delegationId && delegations.data && !delegations.data.some((rule) => rule.id === delegationId && delegationActive(rule))) {
      setDelegationId('');
    }
  }, [delegationId, delegations.data]);

  const storeDetail = useCallback((value: AssistantDetail) => {
    client.setQueryData([...key, 'session', value.session.id], value);
    client.setQueryData<AssistantSession[]>([...key, 'sessions'], (old) => {
      const list = old || [];
      return [value.session, ...list.filter((item) => item.id !== value.session.id)];
    });
    client.setQueryData<AssistantAction[]>([...key, 'actions'], (old) => {
      const incoming = value.actions || [];
      const ids = new Set(incoming.map((item) => item.id));
      return [...incoming, ...(old || []).filter((item) => !ids.has(item.id))];
    });
  // scope is the complete cache identity; key is intentionally recreated only from it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, scope]);

  const inspectPending = useCallback((value: AssistantDetail) => {
    const pending = pendingRef.current;
    if (pending?.sessionId !== value.session.id) return;
    const messages = value.messages || value.session.messages || [];
    const position = messages.findIndex((item) => item.id === pending.input.message_id);
    const completed = position >= 0 && messages.slice(position + 1).some((item) => item.role === 'assistant');
    if (completed) {
      setPendingTurn(null); pendingRef.current = null; setError(null);
    }
  }, []);

  const checkResponse = useCallback(async () => {
    const target = pendingRef.current?.sessionId || selectedId;
    const startedScope = scope;
    try {
      if (target) {
        const value = await operatorAssistantAPI.detail(target);
        if (currentScope.current !== startedScope) return;
        storeDetail(value); inspectPending(value);
      }
      const list = await operatorAssistantAPI.actions();
      if (currentScope.current !== startedScope) return;
      client.setQueryData([...key, 'actions'], list);
      setDecisions((old) => {
        const next = { ...old };
        list.forEach((action) => {
          if (next[action.id]?.phase === 'unknown' && action.status !== 'pending_review') delete next[action.id];
        });
        return next;
      });
    } catch (cause) {
      if (currentScope.current === startedScope) setError(assistantError(cause, 'Unable to check the latest response. No operation has been replayed.'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, selectedId, client, storeDetail, inspectPending]);

  const hasQueuedWork = (actions.data || []).some((action) => ['approved', 'running'].includes(action.status));
  useEffect(() => {
    if (!active || (!pendingTurn && !hasQueuedWork && !Object.keys(decisions).length)) return;
    const interval = window.setInterval(() => {
      if (pendingRef.current?.phase === 'unknown') void checkResponse();
      else {
        void actions.refetch();
        void delegations.refetch();
        if (selectedId) void detail.refetch();
      }
    }, 5000);
    return () => window.clearInterval(interval);
  // Query observers are stable for this scope; the booleans control the polling lease.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, pendingTurn, hasQueuedWork, decisions, selectedId, checkResponse]);
  useEffect(() => { if (detail.data) inspectPending(detail.data); }, [detail.data, inspectPending]);
  useEffect(() => {
    if (!intake.pending) return;
    const found = actions.data?.find((action) => action.request_id === intake.pending?.request_id);
    if (found) setIntake((old) => ({ ...old, receipt: found, pending: null, error: '' }));
  }, [intake.pending, actions.data]);

  const createSession = async () => {
    if (creating || sendingRef.current || pendingRef.current || !config.data?.enabled) return;
    const startedScope = scope;
    setCreating(true); setError(null);
    try {
      const session = await operatorAssistantAPI.createSession();
      if (currentScope.current !== startedScope) return;
      client.setQueryData([...key, 'session', session.id], { session, messages: session.messages || [], actions: [] });
      client.setQueryData<AssistantSession[]>([...key, 'sessions'], (old) => [session, ...(old || [])]);
      setSelectedId(session.id); setDraft(''); setDelegationId('');
    } catch (cause) {
      if (currentScope.current === startedScope) {
        setError(assistantError(cause, 'Unable to open a conversation. Refresh the conversation list before trying again.'));
        void sessions.refetch();
      }
    } finally { if (currentScope.current === startedScope) setCreating(false); }
  };

  const sendTurn = async (pending: PendingTurn) => {
    if (sendingRef.current) return;
    const startedScope = scope;
    sendingRef.current = true;
    pendingRef.current = { ...pending, phase: 'sending' };
    setPendingTurn(pendingRef.current); setError(null);
    try {
      const value = await operatorAssistantAPI.turn(pending.sessionId, pending.input);
      if (currentScope.current !== startedScope) return;
      storeDetail(value);
      pendingRef.current = null; setPendingTurn(null); setDraft('');
      void actions.refetch();
    } catch (cause) {
      if (currentScope.current !== startedScope) return;
      const status = (cause as { response?: { status?: number } })?.response?.status;
      if (status && [400, 403, 404, 409, 413, 422, 429].includes(status)) {
        pendingRef.current = null; setPendingTurn(null);
        setError(assistantError(cause, 'This message could not be accepted.'));
        void detail.refetch();
      } else {
        pendingRef.current = { ...pending, phase: 'unknown' };
        setPendingTurn(pendingRef.current);
        setError(assistantError(cause, 'The response could not be confirmed. Check the conversation before continuing; no operation has been replayed.'));
        await checkResponse();
      }
    } finally { if (currentScope.current === startedScope) sendingRef.current = false; }
  };
  const submit = async () => {
    const session = detail.data?.session;
    if (!session || !draft.trim() || pendingRef.current || !config.data?.ready || !config.data.enabled) return;
    const input: AssistantTurnInput = {
      message_id: assistantRequestId(), message: draft.trim(), expected_revision: session.revision,
    };
    if (delegationId && delegations.data?.some((rule) => rule.id === delegationId && delegationActive(rule))) {
      input.delegation_id = delegationId;
    }
    await sendTurn({ sessionId: session.id, input, phase: 'sending' });
  };
  const retryTurn = async () => {
    const pending = pendingRef.current;
    if (pending?.phase === 'unknown') await sendTurn(pending);
  };
  const submitDecision = async (id: string, approve: boolean, ruleId?: string) => {
    if (decisions[id] || decidingRef.current.has(id)) return;
    decidingRef.current.add(id);
    const startedScope = scope;
    setDecisions((old) => ({ ...old, [id]: { approve, phase: 'sending' } })); setError(null);
    try {
      const action = ruleId ? await operatorAssistantAPI.delegate(id, ruleId) : await operatorAssistantAPI.decide(id, approve);
      if (currentScope.current !== startedScope) return;
      client.setQueryData<AssistantAction[]>([...key, 'actions'], (old) => (old || []).map((item) => item.id === id ? assistantActionSummary(action) : item));
      setDecisions((old) => { const next = { ...old }; delete next[id]; return next; });
      await actions.refetch();
      if (ruleId) await delegations.refetch();
      if (selectedId) await detail.refetch();
    } catch (cause) {
      if (currentScope.current !== startedScope) return;
      const status = (cause as { response?: { status?: number } })?.response?.status;
      if (status && [400, 403, 404, 409, 422].includes(status)) {
        setDecisions((old) => { const next = { ...old }; delete next[id]; return next; });
      } else setDecisions((old) => ({ ...old, [id]: { approve, phase: 'unknown' } }));
      setError(assistantError(cause, 'The decision could not be confirmed. Check its receipt before trying another decision.'));
      await checkResponse();
    } finally { decidingRef.current.delete(id); }
  };
  const createDelegation = async (input: AssistantDelegationInput): Promise<AssistantDelegation | undefined> => {
    if (delegationBusy) return undefined;
    const startedScope = scope;
    setDelegationBusy(true); setError(null);
    try {
      const rule = await operatorAssistantAPI.createDelegation(input);
      if (currentScope.current !== startedScope) return undefined;
      await delegations.refetch(); return rule;
    } catch (cause) {
      if (currentScope.current === startedScope) {
        setError(assistantError(cause, 'The delegation could not be confirmed. Refresh the list before enabling it again.'));
        await delegations.refetch();
      }
      return undefined;
    } finally { if (currentScope.current === startedScope) setDelegationBusy(false); }
  };
  const pauseDelegation = async (id: string) => {
    if (delegationBusy) return;
    const startedScope = scope;
    setDelegationBusy(true); setError(null);
    try {
      await operatorAssistantAPI.pauseDelegation(id);
      if (currentScope.current === startedScope) await delegations.refetch();
    } catch (cause) {
      if (currentScope.current === startedScope) {
        setError(assistantError(cause, 'The pause could not be confirmed. Refresh the rule before continuing.'));
        await delegations.refetch();
      }
    } finally { if (currentScope.current === startedScope) setDelegationBusy(false); }
  };
  const prepareIntake = async (input: AssistantIntakeInput): Promise<AssistantAction | undefined> => {
    if (intakeLock.current) return undefined;
    const startedScope = scope;
    intakeLock.current = true;
    setIntake((old) => ({ ...old, busy: true, error: '' }));
    try {
      const result = await operatorAssistantAPI.intake(input);
      if (currentScope.current !== startedScope) return undefined;
      setIntake((old) => ({ ...old, receipt: assistantActionSummary(result.action), pending: null }));
      client.setQueryData<AssistantAction[]>([...key, 'actions'], (old) => [assistantActionSummary(result.action), ...(old || []).filter((action) => action.id !== result.action.id)]);
      void actions.refetch();
      return result.action;
    } catch (cause) {
      if (currentScope.current !== startedScope) return undefined;
      const status = (cause as { response?: { status?: number } })?.response?.status;
      setIntake((old) => ({ ...old,
        pending: status && [400, 403, 404, 413, 422, 429].includes(status) ? null : input,
        error: assistantError(cause, 'The intake outcome could not be confirmed. Check receipts; any retry must retain this source, row keys and request ID.'),
      }));
      void actions.refetch();
      return undefined;
    } finally {
      if (currentScope.current === startedScope) setIntake((old) => ({ ...old, busy: false }));
      intakeLock.current = false;
    }
  };
  const refresh = async () => {
    const startedScope = scope;
    setError(null);
    for (const query of [config, sessions, actions, delegations]) {
      if (currentScope.current !== startedScope) return;
      await query.refetch();
    }
    if (currentScope.current !== startedScope) return;
    if (selectedId || pendingRef.current) await checkResponse();
  };

  const value: AssistantState = {
    eligible, drawerOpen, openDrawer: () => {
      if (isAssistantPage) document.getElementById('assistant-message-page')?.focus();
      else setDrawerOpen(true);
    }, closeDrawer: () => setDrawerOpen(false),
    configuration: config.data, loading: config.isLoading && active,
    error: error || (config.isError ? assistantError(config.error, 'Unable to load MODAL Assistant.') : null),
    dismissError: () => setError(null),
    sessions: sessions.data || [], selectedId,
    selectSession: (id) => { setSelectedId(id); setDraft(''); setDelegationId(''); setError(null); },
    detail: detail.data, detailLoading: detail.isLoading && !!selectedId,
    detailError: detail.isError ? assistantError(detail.error, 'Unable to load this conversation.') :
      sessions.isError ? assistantError(sessions.error, 'Unable to load conversations.') : null,
    creating, createSession, draft, setDraft, pendingTurn, submit, retryTurn, checkResponse,
    actions: actions.data || [], actionsLoading: actions.isLoading && readable,
    actionsError: actions.isError ? assistantError(actions.error, 'Unable to load action receipts.') : null,
    decisions, decide: (id, approve) => submitDecision(id, approve), delegate: (id, ruleId) => submitDecision(id, true, ruleId),
    delegations: delegations.data || [], delegationsLoading: delegations.isLoading && readable,
    delegationsError: delegations.isError ? assistantError(delegations.error, 'Unable to load delegations.') : null,
    delegationId, setDelegationId, createDelegation, pauseDelegation, delegationBusy,
    intake, updateIntake: (fields) => setIntake((old) => ({ ...old, ...fields })), prepareIntake, refresh,
  };
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useOperatorAssistant(): AssistantState {
  const context = useContext(Context);
  if (!context) throw new Error('MODAL Assistant requires its operator workspace provider.');
  return context;
}
